// Histórico de versões de uma proposta/venda gerada (15/09/2026). Doc: docs/propostas-versoes.md
// Cada linha é como o documento estava ANTES de uma gravação (da tela, do Dwight ou do
// sistema). Dá para ver o que muda e restaurar. Incidente que motivou: a PRP-2026-0110
// foi sobrescrita por engano e não havia nada guardado.
//
// ⚠️ Declarar este modal FORA do overlay do ViewerProposta (mesmo motivo do modal de
// assinatura): clique em portal sobe pela árvore de componentes e fecharia o viewer.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, App as AntApp, Button, Col, Empty, Modal, Row, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import { API_URL } from '../../config';
import { getToken } from './utils';
import {
  camposDiferentes, diffBlocos, itensParaLeitura, rotuloCampo, rotuloOrigem, TrechoDiff, valorParaBlocos,
} from '../../utils/diffTexto';
import { listarAreas } from './apiCatalogo';

const { Text } = Typography;

interface VersaoResumo {
  id: number;
  proposta_gerada_id: string;
  numero_proposta: string | null;
  revisao: number | null;
  status: string | null;
  titulo: string | null;
  evento: 'edicao' | 'exclusao';
  origem: string;
  autor_email: string | null;
  campos_alterados: string[];
  criado_em: string;
  hash_antes: string;
  hash_depois: string | null;
  tamanho_html: number;
}

interface Listagem {
  documento: { id: string; numero_proposta: string | null; revisao: number; hash_atual: string } | null;
  revisoes: { id: string; revisao: number; status: string }[];
  versoes: VersaoResumo[];
}

type Mudanca = { campo: string; trechos: TrechoDiff[]; aproximado: boolean };

function lerCorpo(res: any) {
  const d = res?.data;
  if (d && typeof d.body === 'string') { try { return JSON.parse(d.body); } catch { return d; } }
  return d;
}

function mensagemErro(e: any): string {
  const d = lerCorpo(e?.response);
  return d?.error || e?.message || 'erro desconhecido';
}

function codigoErro(e: any): string | undefined {
  return lerCorpo(e?.response)?.codigo;
}

const fmtQuando = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

async function chamar(action: string, body: Record<string, unknown>) {
  const token = await getToken();
  const res = await axios.post(`${API_URL}/comercial/propostas-geradas`, { action, ...body }, { headers: { Authorization: token } });
  return lerCorpo(res);
}

function Trechos({ trechos }: { trechos: TrechoDiff[] }) {
  // Parágrafos iguais em sequência viram "… N iguais …" para o que muda aparecer.
  const blocos: React.ReactNode[] = [];
  let iguais: string[] = [];
  const despejarIguais = (chave: string) => {
    if (!iguais.length) return;
    if (iguais.length <= 2) iguais.forEach((t, k) => blocos.push(<div key={`${chave}-${k}`} style={{ color: '#666', padding: '2px 6px' }}>{t}</div>));
    else blocos.push(<div key={chave} style={{ color: '#999', padding: '2px 6px', fontStyle: 'italic' }}>… {iguais.length} parágrafos iguais …</div>);
    iguais = [];
  };
  trechos.forEach((t, idx) => {
    if (t.tipo === 'igual') { iguais.push(t.texto); return; }
    despejarIguais(`i${idx}`);
    const sai = t.tipo === 'removido';
    blocos.push(
      <div key={idx} style={{
        padding: '3px 6px', margin: '2px 0', borderRadius: 3,
        background: sai ? '#FDECEC' : '#E8F6EE', color: sai ? '#9B1C1C' : '#0A5C36',
        textDecoration: sai ? 'line-through' : 'none',
      }}>
        {sai ? '− ' : '+ '}{t.texto}
      </div>,
    );
  });
  despejarIguais('fim');
  return <div style={{ fontSize: 12, lineHeight: 1.45 }}>{blocos}</div>;
}

export default function ModalHistoricoProposta({ open, propostaGeradaId, rotulo, onClose, onRestaurada }: {
  open: boolean;
  propostaGeradaId: string;
  rotulo: string;
  onClose: () => void;
  onRestaurada: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [lista, setLista] = useState<Listagem | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionada, setSelecionada] = useState<number | null>(null);
  const [detalhe, setDetalhe] = useState<any>(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [vista, setVista] = useState<'mudancas' | 'documento'>('mudancas');
  const [restaurando, setRestaurando] = useState(false);
  const detalheRef = useRef<HTMLDivElement>(null);
  // Nomes das sub-áreas, para a comparação mostrar "Batimetria" e não o
  // identificador que a linha guarda. Se não vier, a comparação segue com ele.
  const [subareas, setSubareas] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!open) return;
    listarAreas()
      .then(areas => setSubareas(Object.fromEntries(areas.flatMap(a => a.subareas.map(s => [s.id, s.nome])))))
      .catch(() => { /* fica o identificador */ });
  }, [open]);

  async function carregar() {
    setCarregando(true); setErro(null);
    try {
      const d = await chamar('pg_versoes_listar', { proposta_gerada_id: propostaGeradaId });
      setLista(d);
      setSelecionada(atual => atual ?? d?.versoes?.[0]?.id ?? null);
    } catch (e: any) { setErro(mensagemErro(e)); }
    finally { setCarregando(false); }
  }

  useEffect(() => {
    if (!open) return;
    setLista(null); setSelecionada(null); setDetalhe(null); setVista('mudancas');
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propostaGeradaId]);

  useEffect(() => {
    if (!open || selecionada === null) return;
    let cancelado = false;
    setCarregandoDetalhe(true); setDetalhe(null);
    // Compara com o documento ABERTO (a versão pode ser de outra revisão da mesma proposta).
    chamar('pg_versao_obter', { versao_id: selecionada, destino_id: propostaGeradaId })
      .then(d => { if (!cancelado) setDetalhe(d); })
      .catch(e => { if (!cancelado) message.error(`Não abriu a versão: ${mensagemErro(e)}`); })
      .finally(() => { if (!cancelado) setCarregandoDetalhe(false); });
    return () => { cancelado = true; };
  }, [open, selecionada, message, propostaGeradaId]);

  const resumo = lista?.versoes.find(v => v.id === selecionada) || null;
  const revisaoDe = (id: string) => lista?.revisoes.find(r => r.id === id)?.revisao;
  const ehDesteDocumento = resumo?.proposta_gerada_id === propostaGeradaId;
  const igualAoAtual = !!detalhe?.igual_ao_atual;
  const documentoSumiu = !!detalhe && !detalhe.atual;
  // Revisão aberta já substituída: restaurar aqui gravaria onde ninguém olha.
  const revisaoMaisNova = useMemo(() => {
    const aberta = lista?.documento?.revisao ?? 0;
    const maior = Math.max(aberta, ...(lista?.revisoes || []).map(r => r.revisao ?? 0));
    return maior > aberta ? maior : null;
  }, [lista]);

  // O que muda se restaurar: do conteúdo ATUAL para esta versão.
  const mudancas = useMemo(() => {
    if (!detalhe?.versao || !detalhe?.atual) return [];
    const v = detalhe.versao;
    const a = detalhe.atual;
    const nomes: Record<string, string> = detalhe.composicoes || {};
    const itens: Mudanca[] = [];
    if ((v.titulo || '') !== (a.titulo || '')) {
      itens.push({ campo: 'titulo', ...diffBlocos(valorParaBlocos(a.titulo), valorParaBlocos(v.titulo)) });
    }
    // O título dentro dos dados é o mesmo texto do título: uma linha só.
    for (const campo of camposDiferentes(a.dados_estruturados, v.dados_estruturados, ['titulo'])) {
      const r = diffBlocos(valorParaBlocos(a.dados_estruturados?.[campo]), valorParaBlocos(v.dados_estruturados?.[campo]));
      itens.push({ campo, ...r });
    }
    if (JSON.stringify(a.itens_venda || []) !== JSON.stringify(v.itens_venda || [])) {
      // Sub-área cujo nome não veio (a lista ainda carrega, ou falhou): um apelido
      // estável ("outra sub-área 1") — a troca continua visível, sem identificador na tela.
      const apelidos = new Map<string, string>();
      const nomeDaSubarea = (id: string) => {
        if (subareas[id]) return subareas[id];
        if (!apelidos.has(id)) apelidos.set(id, `outra sub-área ${apelidos.size + 1}`);
        return apelidos.get(id);
      };
      const legivel = (lista: unknown) => valorParaBlocos(itensParaLeitura(lista, nomeDaSubarea));
      itens.push({ campo: 'itens_venda', ...diffBlocos(legivel(a.itens_venda), legivel(v.itens_venda)) });
    }
    if ((a.composicao_id || null) !== (v.composicao_id || null)) {
      const nome = (id: string | null) => (!id ? 'nenhuma' : nomes[id] || 'composição apagada — fica a de agora');
      itens.push({
        campo: 'composicao_id', aproximado: false,
        trechos: [
          { tipo: 'removido', texto: nome(a.composicao_id) },
          { tipo: 'adicionado', texto: nome(v.composicao_id) },
        ],
      });
    }
    return itens;
  }, [detalhe, subareas]);

  function escolher(id: number) {
    setSelecionada(id);
    setVista('mudancas');
    // No celular o detalhe fica embaixo da lista: levar a pessoa até ele.
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 991px)').matches) {
      setTimeout(() => detalheRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }

  function confirmarRestauracao() {
    if (!resumo || !lista?.documento) return;
    const deOutraRevisao = !ehDesteDocumento;
    const revOrigem = revisaoDe(resumo.proposta_gerada_id) ?? resumo.revisao;
    modal.confirm({
      title: deOutraRevisao
        ? `Trazer para ${rotulo} o conteúdo da R${revOrigem ?? '?'} de ${fmtQuando(resumo.criado_em)}?`
        : `Restaurar ${rotulo} para como estava em ${fmtQuando(resumo.criado_em)}?`,
      content: (
        <div>
          <p style={{ margin: '0 0 6px' }}>O conteúdo atual não se perde: ele vira uma versão neste histórico, e dá para voltar a ele.</p>
          <p style={{ margin: '0 0 6px' }}>Se o link público estiver ativo, o cliente passa a ver o conteúdo restaurado.</p>
          <p style={{ margin: 0 }}>PDF já baixado ou enviado antes <b>não muda</b>: gere de novo se for mandar.</p>
        </div>
      ),
      okText: deOutraRevisao ? 'Trazer para esta revisão' : 'Restaurar',
      cancelText: 'Cancelar',
      zIndex: 2100,
      onOk: async () => {
        setRestaurando(true);
        try {
          await chamar('pg_versao_restaurar', {
            versao_id: resumo.id, if_hash_atual: lista.documento!.hash_atual, destino_id: propostaGeradaId,
          });
          message.success('Restaurado. O conteúdo anterior ficou guardado no histórico.');
          onRestaurada();
          setSelecionada(null);
          await carregar();
        } catch (e: any) {
          if (codigoErro(e) === 'mudou_depois') {
            // Alguém gravou enquanto o histórico estava aberto: recarregar e deixar a pessoa conferir.
            message.warning('O documento mudou enquanto o histórico estava aberto. Recarreguei — confira antes de restaurar.', 8);
            setDetalhe(null);
            await carregar();
            const atual = selecionada;
            setSelecionada(null);
            setTimeout(() => setSelecionada(atual), 0);
          } else {
            message.error(`Não restaurou: ${mensagemErro(e)}`, 8);
          }
        } finally { setRestaurando(false); }
      },
    });
  }

  const colunas = [
    {
      title: 'Quando', dataIndex: 'criado_em', key: 'q', width: 128,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{fmtQuando(v)}</Text>,
    },
    {
      title: 'O que aconteceu', key: 'o',
      render: (_: unknown, r: VersaoResumo) => {
        const rev = revisaoDe(r.proposta_gerada_id);
        const outraRevisao = r.proposta_gerada_id !== propostaGeradaId;
        return (
          <Space orientation="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>
              {r.evento === 'exclusao' ? 'Excluída por ' : 'Gravação de '}<b>{rotuloOrigem(r.origem, r.autor_email)}</b>
            </Text>
            <Space size={4} wrap>
              {r.evento === 'edicao' && r.campos_alterados.map(c => <Tag key={c} style={{ fontSize: 10, margin: 0 }}>{rotuloCampo(c)}</Tag>)}
              {outraRevisao && <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>{rev !== undefined ? `R${rev}` : `R${r.revisao ?? '?'}`}</Tag>}
              {r.evento === 'exclusao' && <Tag color="red" style={{ fontSize: 10, margin: 0 }}>excluída</Tag>}
            </Space>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={`Histórico — ${rotulo}`}
      width="94vw"
      style={{ top: 16, maxWidth: 1400 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto', overflowX: 'hidden' } }}
      zIndex={2000}
      destroyOnHidden
    >
      {erro && <Alert type="error" showIcon title="Não carregou o histórico" description={erro} style={{ marginBottom: 12 }} />}
      {carregando && !lista ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : null}
      {lista && !lista.versoes.length && (
        <Empty description="Nenhuma versão guardada ainda. O histórico começa na primeira gravação depois de 15/09/2026." />
      )}
      {lista && lista.versoes.length > 0 && (
        <Row gutter={16}>
          <Col xs={24} lg={9}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
              Cada linha é como o documento estava <b>antes</b> daquela gravação. Guardado por 12 meses.
            </Text>
            <Table
              rowKey="id" size="small" pagination={false} columns={colunas} dataSource={lista.versoes}
              scroll={{ y: 'clamp(240px, calc(100vh - 260px), 900px)' }}
              rowClassName={(r: VersaoResumo) => (r.id === selecionada ? 'ant-table-row-selected' : '')}
              onRow={(r: VersaoResumo) => ({ onClick: () => escolher(r.id), style: { cursor: 'pointer' } })}
            />
          </Col>
          <Col xs={24} lg={15}>
            {!resumo ? <Empty description="Escolha uma versão à esquerda." /> : (
              <div ref={detalheRef}>
                <Space wrap style={{ marginBottom: 10, width: '100%', justifyContent: 'space-between' }}>
                  <Text strong style={{ color: '#020D7F' }}>
                    Como estava antes da gravação de {fmtQuando(resumo.criado_em)}
                  </Text>
                  <Space wrap>
                    <Segmented
                      size="small" value={vista} onChange={v => setVista(v as 'mudancas' | 'documento')}
                      options={[{ label: 'O que muda se restaurar', value: 'mudancas' }, { label: 'Ver o documento', value: 'documento' }]}
                    />
                    <Button
                      type="primary" size="small" loading={restaurando} onClick={confirmarRestauracao}
                      disabled={!detalhe || igualAoAtual || documentoSumiu || revisaoMaisNova !== null || carregandoDetalhe}
                    >
                      {ehDesteDocumento ? 'Restaurar esta versão' : 'Trazer para esta revisão'}
                    </Button>
                  </Space>
                </Space>
                {revisaoMaisNova !== null && (
                  <Alert type="warning" showIcon style={{ marginBottom: 10 }}
                    title={`Existe a R${revisaoMaisNova}, mais nova.`}
                    description={`Esta revisão já foi substituída. Para usar esta versão, abra a R${revisaoMaisNova} e restaure por lá — o histórico dela mostra as versões desta.`} />
                )}
                {!ehDesteDocumento && revisaoMaisNova === null && (
                  <Alert type="info" showIcon style={{ marginBottom: 10 }}
                    title={`Esta versão é da ${revisaoDe(resumo.proposta_gerada_id) !== undefined ? `R${revisaoDe(resumo.proposta_gerada_id)}` : `R${resumo.revisao ?? '?'}`}${resumo.evento === 'exclusao' ? ', que foi excluída' : ''}.`}
                    description="Dá para trazer o conteúdo dela para a revisão aberta; a numeração desta revisão não muda." />
                )}
                {documentoSumiu && (
                  <Alert type="error" showIcon style={{ marginBottom: 10 }}
                    title="O documento aberto foi excluído."
                    description="Veja ou copie o conteúdo em “Ver o documento”; não há onde restaurar." />
                )}
                {igualAoAtual && <Alert type="success" showIcon style={{ marginBottom: 10 }} title="Igual ao conteúdo atual." />}
                {carregandoDetalhe && <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>}
                {detalhe && vista === 'mudancas' && (
                  mudancas.length === 0
                    ? <Alert type="info" showIcon title="Textos e valores iguais aos de agora" description="A diferença está só na montagem do documento (data, dados da empresa ou do cliente). Veja em “Ver o documento”." />
                    : (
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
                          <span style={{ color: '#9B1C1C' }}>− vermelho</span>: está hoje e sai ·{' '}
                          <span style={{ color: '#0A5C36' }}>+ verde</span>: estava nesta versão e volta
                        </Text>
                        {mudancas.map(m => (
                          <div key={m.campo} style={{ border: '0.5px solid #e3e3e8', borderRadius: 5, padding: 10, marginBottom: 10 }}>
                            <Text strong style={{ fontSize: 12 }}>{rotuloCampo(m.campo)}</Text>
                            {m.aproximado && <Text type="secondary" style={{ fontSize: 11 }}> — texto grande: mostrando a troca inteira</Text>}
                            <Trechos trechos={m.trechos} />
                          </div>
                        ))}
                      </div>
                    )
                )}
                {detalhe && vista === 'documento' && (
                  <iframe
                    title="Versão do documento"
                    sandbox=""
                    srcDoc={detalhe.versao?.conteudo_final || '<p>(sem documento montado)</p>'}
                    style={{ width: '100%', height: 'clamp(320px, calc(100vh - 250px), 1100px)', border: '0.5px solid #e0e0e0', borderRadius: 5, background: '#fff' }}
                  />
                )}
              </div>
            )}
          </Col>
        </Row>
      )}
    </Modal>
  );
}
